import { useAccount, useReadContracts } from "wagmi";
import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ONE_MINUTE_MS } from "../utils/time";
import { mainnetBorrowContracts, testnetBorrowContracts, } from "../lib/contracts";
import { usePassportContext } from "./usePassportContext";
import { CHAIN_ID } from "../constants";
// Wagmi handles typesafety with ABI const assertions. TypeScript doesn't
// support importing JSON as const yet so types cannot be inferred from the
// imported contract. As a workaround there is minimal ABI definition that can
// be asserted types from.
// Ref: https://wagmi.sh/core/typescript#const-assert-abis-typed-data
const TROVE_MANAGER_ABI = [
    {
        inputs: [
            {
                internalType: "address",
                name: "_borrower",
                type: "address",
            },
        ],
        name: "getEntireDebtAndColl",
        outputs: [
            {
                internalType: "uint256",
                name: "coll",
                type: "uint256",
            },
            {
                internalType: "uint256",
                name: "principal",
                type: "uint256",
            },
            {
                internalType: "uint256",
                name: "interest",
                type: "uint256",
            },
            {
                internalType: "uint256",
                name: "pendingCollateral",
                type: "uint256",
            },
            {
                internalType: "uint256",
                name: "pendingPrincipal",
                type: "uint256",
            },
            {
                internalType: "uint256",
                name: "pendingInterest",
                type: "uint256",
            },
        ],
        stateMutability: "view",
        type: "function",
    },
];
const BORROWER_OPERATIONS_ABI = [
    {
        inputs: [],
        name: "MUSD_GAS_COMPENSATION",
        outputs: [
            {
                internalType: "uint256",
                name: "",
                type: "uint256",
            },
        ],
        stateMutability: "view",
        type: "function",
    },
];
/**
 * Query hook for getting borrow data. Returns collateral and trove debt for the
 * connected account, based on it's evm address.
 * @param queryOptions Query options passed to the underlying `useQuery` hook.
 */
export function useBorrowData(queryOptions = {}) {
    const { environment = "mainnet" } = usePassportContext();
    const { address } = useAccount();
    const contractAddress = useMemo(() => {
        if (environment === "mainnet") {
            return {
                troveManager: mainnetBorrowContracts.TroveManager.address,
                borrowerOperations: mainnetBorrowContracts.BorrowerOperations.address,
            };
        }
        return {
            troveManager: testnetBorrowContracts.TroveManager.address,
            borrowerOperations: testnetBorrowContracts.BorrowerOperations.address,
        };
    }, [environment]);
    const chainId = CHAIN_ID[environment];
    return useReadContracts({
        contracts: [
            {
                abi: TROVE_MANAGER_ABI,
                address: contractAddress.troveManager,
                functionName: "getEntireDebtAndColl",
                args: [address || "0x"],
                chainId,
            },
            {
                abi: BORROWER_OPERATIONS_ABI,
                address: contractAddress.borrowerOperations,
                functionName: "MUSD_GAS_COMPENSATION",
                args: [],
                chainId,
            },
        ],
        query: {
            enabled: !!address,
            staleTime: 5 * ONE_MINUTE_MS,
            retry: 1,
            select: (data) => {
                if (!data || data.some((item) => !item.result))
                    return undefined;
                const [collateral, principal, interest] = data[0].result;
                const gasCompensation = data[1].result;
                return {
                    collateral,
                    // Principal includes gas compensation value which is not
                    troveDebt: principal + interest - gasCompensation,
                };
            },
            ...queryOptions,
        },
    });
}
/**
 * Hook for for invalidating current user's borrow data. Can be used to
 * invalidate borrow data manually, which forces the data to be re-fetched.
 * @returns Function `invalidateBorrowData` for invalidating the borrow data
 */
export function useInvalidateBorrowData() {
    const queryClient = useQueryClient();
    const { queryKey } = useBorrowData();
    const invalidateBorrowDataHandler = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);
    return {
        invalidateBorrowData: invalidateBorrowDataHandler,
    };
}
/**
 * Hook for for resetting current user's borrow data. Can be used to reset
 * borrow data manually, which forces the data to be re-fetched.
 * @returns Function `resetBorrowData` for resetting the borrow data
 */
export function useResetBorrowData() {
    const queryClient = useQueryClient();
    const { queryKey } = useBorrowData();
    const resetBorrowDataHandler = useCallback(() => queryClient.resetQueries({ queryKey }), [queryClient, queryKey]);
    return {
        resetBorrowData: resetBorrowDataHandler,
    };
}
//# sourceMappingURL=useBorrowData.js.map